/**
 * Configuration State Slice
 *
 * Manages the main cluster configuration state including GPU platform,
 * server settings, network design, rack configuration, and power settings.
 */

import type { StateCreator } from 'zustand';
import type {
  UIClusterConfiguration,
  UIGPUPlatformConfig,
  UIServerConfig,
  UINetworkDesign,
  UIScalingConfig,
  UIRackConfig,
  UIPowerConfig,
} from '../types/ui-configuration';
import { generateUUID } from '../utils/uuid';

export interface ConfigurationSlice {
  // State
  configuration: UIClusterConfiguration;

  // Actions
  setConfiguration: (config: UIClusterConfiguration) => void;
  updateConfiguration: (updates: Partial<UIClusterConfiguration>) => void;

  // Scaling
  setTotalGPUs: (gpus: number) => void;
  setGPUsPerNode: (gpus: number) => void;
  updateScaling: (updates: Partial<UIScalingConfig>) => void;

  // GPU Platform
  updateGPUPlatform: (updates: Partial<UIGPUPlatformConfig>) => void;

  // Server
  updateServer: (updates: Partial<UIServerConfig>) => void;

  // Network
  updateNetwork: (updates: Partial<UINetworkDesign>) => void;

  // Rack
  updateRack: (updates: Partial<UIRackConfig>) => void;

  // Power
  updatePower: (updates: Partial<UIPowerConfig>) => void;

  // Additional Nodes
  updateAdditionalNodes: (updates: Partial<UIAdditionalNodeConfig>) => void;

  // Reset
  resetConfiguration: () => void;

  // Dirty state
  markDirty: () => void;
  markClean: () => void;
}

const DEFAULT_CONFIGURATION: UIClusterConfiguration = {
  id: generateUUID(),
  name: 'Untitled Configuration',
  description: '',
  createdAt: new Date(),
  modifiedAt: new Date(),
  isDirty: false,

  scaling: {
    totalGPUs: 2000,
    gpusPerNode: 8,
    totalNodes: 250,
    totalComputeRacks: 125,
  },

  gpu: {
    platformId: 'hgx-b200',
    platformName: 'NVIDIA HGX B200',
    family: 'HGX',
    gpusPerPlatform: 8,
    interconnect: 'NVLink',
    requiresLiquidCooling: true,
    tdpWatts: 1000,
    memoryGB: 192,
    memoryType: 'HBM3e',
  },

  server: {
    serverId: 'custom',
    manufacturer: 'Custom',
    model: 'Custom Server',
    formFactor: '10U',
    rackUnits: 10,
    cpuCount: 2,
    cpuModel: '',
    cpuTdpWatts: 350,
    ramGB: 2048,
    driveBays: 8,
    ethernetPorts: 2,
    networkPorts: {
      ethernet1g: 1,
      ethernet10g: 0,
      ethernet25g: 2,
      ethernet100g: 0,
      ethernet400g: 0,
      infiniband: 8,
    },
    powerSupplies: {
      count: 8,
      wattage: 3000,
      redundancy: 'N+N',
      efficiency: 0.96,
    },
    weight: {
      emptyLbs: 200,
      maxConfigLbs: 350,
    },
  },

  network: {
    topology: 'fat-tree',
    tiers: 2,

    eastWest: {
      enabled: true,
      fabricType: 'InfiniBand',
      speedGbps: 800,
      leafSwitch: {
        manufacturer: 'NVIDIA',
        model: 'Quantum-X800 (Q3400)',
        ports: 144,
        formFactor: '1U',
        powerWatts: 850,
      },
      spineSwitch: {
        manufacturer: 'NVIDIA',
        model: 'Quantum-X800 (Q3400)',
        ports: 144,
        formFactor: '1U',
        powerWatts: 850,
      },
      topologyType: 'fat-tree',
      railsCount: 4,
      dragonflyGroups: 8,
      dragonflyRoutersPerGroup: 16,
      blockingFactor: 1.0, // 1.0 = non-blocking
      portsPerNode: 8,
      oversubscription: 1.0,
      routing: {
        algorithm: 'adaptive',
        enableSHARP: true,
        enableCongestionControl: true,
        qosLevels: 8,
        enableDynamicRerouting: true,
        enablePathRandomization: true,
      },
    },

    northSouth: {
      enabled: true,
      fabricType: 'Ethernet',
      speedGbps: 400,
      leafSwitch: {
        manufacturer: 'NVIDIA',
        model: 'SN5600',
        ports: 128,
        formFactor: '2U',
        powerWatts: 650,
      },
      spineSwitch: {
        manufacturer: 'NVIDIA',
        model: 'SN5600',
        ports: 128,
        formFactor: '2U',
        powerWatts: 650,
      },
      topologyType: 'fat-tree',
      railsCount: 4,
      dragonflyGroups: 8,
      dragonflyRoutersPerGroup: 16,
      blockingFactor: 1.0, // Non-blocking (was downlinkRatio: 0.5)
      highAvailabilitySpine: false, // Disabled by default
      portsPerNode: 2,
      oversubscription: 1.0,
    },

    storage: {
      enabled: false,
    },

    management: {
      enabled: true,
      fabricType: 'Ethernet',
      speedGbps: 1,
      switchModel: 'NVIDIA SN2201',
      portsPerSwitch: 48,
      portsPerNode: 1,
      dpusPerNode: 2,
      oobPortsPerDPU: 1,
    },
  },

  rack: {
    standardRackHeight: 48,
    usableRackHeight: 45,
    serversPerRack: 2,
    pduReservedUnits: 2,
    cableMgmtReservedUnits: 1,
    otherReservedUnits: 0,
  },

  power: {
    powerPerNodeKw: 14.3,
    pduEfficiency: 0.98,
    upsEfficiency: 0.96,
    coolingOverhead: 1.3,
    includeSwitchPower: true,
    utilityVoltage: '480V 3-phase',
  },

  additionalNodes: {
    cpuNodes: {
      enabled: false,
      profileId: 'custom-cpu', // Default to custom profile
      count: 0,
    },
    infraNodes: {
      enabled: false,
      profileId: 'custom-infra', // Default to custom profile
      count: 0,
    },
  },

  app: {
    version: '1.0.0',
    theme: 'light',
    units: 'metric',
    currency: 'USD',
    locale: 'en-US',
    csv: {
      delimiter: ',',
      includeHeader: true,
      quoteChar: '"',
      lineEnding: '\n',
    },
    enableAdvancedFeatures: false,
    showDerivations: true,
    autoSave: true,
  },
};

export const createConfigurationSlice: StateCreator<
  ConfigurationSlice,
  [],
  [],
  ConfigurationSlice
> = (set) => ({
  configuration: DEFAULT_CONFIGURATION,

  setConfiguration: (config) =>
    set({
      configuration: {
        ...config,
        modifiedAt: new Date(),
      },
    }),

  updateConfiguration: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        ...updates,
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  setTotalGPUs: (gpus) =>
    set((state) => {
      const totalNodes = Math.ceil(gpus / state.configuration.scaling.gpusPerNode);
      const totalComputeRacks = Math.ceil(totalNodes / state.configuration.rack.serversPerRack);

      return {
        configuration: {
          ...state.configuration,
          scaling: {
            ...state.configuration.scaling,
            totalGPUs: gpus,
            totalNodes,
            totalComputeRacks,
          },
          modifiedAt: new Date(),
          isDirty: true,
        },
      };
    }),

  setGPUsPerNode: (gpus) =>
    set((state) => {
      const totalNodes = Math.ceil(state.configuration.scaling.totalGPUs / gpus);
      const totalComputeRacks = Math.ceil(totalNodes / state.configuration.rack.serversPerRack);

      return {
        configuration: {
          ...state.configuration,
          scaling: {
            ...state.configuration.scaling,
            gpusPerNode: gpus,
            totalNodes,
            totalComputeRacks,
          },
          modifiedAt: new Date(),
          isDirty: true,
        },
      };
    }),

  updateScaling: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        scaling: {
          ...state.configuration.scaling,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  updateGPUPlatform: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        gpu: {
          ...state.configuration.gpu,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  updateServer: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        server: {
          ...state.configuration.server,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  updateNetwork: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        network: {
          ...state.configuration.network,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  updateRack: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        rack: {
          ...state.configuration.rack,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  updatePower: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        power: {
          ...state.configuration.power,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  updateAdditionalNodes: (updates) =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        additionalNodes: {
          ...state.configuration.additionalNodes,
          ...updates,
        },
        modifiedAt: new Date(),
        isDirty: true,
      },
    })),

  resetConfiguration: () =>
    set({
      configuration: {
        ...DEFAULT_CONFIGURATION,
        id: generateUUID(),
        createdAt: new Date(),
        modifiedAt: new Date(),
      },
    }),

  markDirty: () =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        isDirty: true,
      },
    })),

  markClean: () =>
    set((state) => ({
      configuration: {
        ...state.configuration,
        isDirty: false,
      },
    })),
});
